/**
 * WebOs Initial FileSystem
 *
 * Crée la structure de base du système de fichiers au premier boot.
 */

import { createLogger } from '../utils/debug.js';
import { getFileSystem, FileType } from './filesystem.js';

const log = createLogger('FS');

// Version du système de fichiers (pour migrations futures)
const FS_VERSION = '1.0.0';

/**
 * Structure initiale du système de fichiers
 */
const INITIAL_STRUCTURE = {
  directories: [
    '/bin',
    '/etc',
    '/etc/webos',
    '/home',           // Les sous-répertoires seront créés par l'assistant utilisateur
    '/tmp',
    '/usr',
    '/usr/bin',
    '/usr/lib',
    '/usr/lib/webos-ui',
    '/usr/share',
    '/usr/share/man',
    '/var',
    '/var/log',
    '/var/cache',
    '/var/lib',
    '/var/lib/webpkg',
    '/var/run'
  ],

  files: [
    {
      path: '/etc/hostname',
      content: 'webos'
    },
    {
      path: '/etc/webos/version',
      content: FS_VERSION
    },
    // Note: /etc/passwd, /etc/shadow, /etc/sudoers sont créés par l'assistant premier boot
    {
      path: '/etc/environment',
      content: 'LANG=fr_FR.UTF-8\nTERM=xterm-256color\n'
    },
    {
      path: '/var/lib/webpkg/installed.json',
      content: '{"packages":{}}'
    },
    {
      path: '/var/log/auth.log',
      content: ''
    }
  ]
};

/**
 * Vérifie si le système de fichiers a déjà été initialisé
 * @returns {Promise<boolean>}
 */
export async function isInitialized() {
  const fs = getFileSystem();

  try {
    await fs.stat('/etc/webos/version');
    return true;
  } catch (e) {
    if (e.code === 'ENOENT') {
      return false;
    }
    throw e;
  }
}

/**
 * Crée la structure initiale du système de fichiers
 * @returns {Promise<void>}
 */
export async function createInitialStructure() {
  const fs = getFileSystem();
  const timer = log.time('Create initial structure');

  log.info('Creating initial filesystem structure...');

  // Créer le dossier racine
  try {
    await fs.mkdir('/');
  } catch (e) {
    // Ignorer si existe déjà
  }

  // Créer tous les répertoires
  const dirGroup = log.group('Creating directories');
  for (const dir of INITIAL_STRUCTURE.directories) {
    try {
      await fs.mkdir(dir, { recursive: true });
      log.debug('Created:', dir);
    } catch (e) {
      if (e.code !== 'EEXIST') {
        log.error('Failed to create directory:', dir, e.message);
      }
    }
  }
  dirGroup.end();

  // Créer tous les fichiers
  const fileGroup = log.group('Creating files');
  for (const file of INITIAL_STRUCTURE.files) {
    try {
      await fs.writeFile(file.path, file.content);
      log.debug('Created:', file.path);
    } catch (e) {
      log.error('Failed to create file:', file.path, e.message);
    }
  }
  fileGroup.end();

  timer.end();
  log.info('Initial filesystem structure created successfully');
}

/**
 * Vérifie et répare la structure si nécessaire
 * @returns {Promise<void>}
 */
export async function verifyStructure() {
  const fs = getFileSystem();
  const timer = log.time('Verify structure');

  log.info('Verifying filesystem structure...');

  let repaired = 0;

  // Vérifier les répertoires essentiels
  for (const dir of INITIAL_STRUCTURE.directories) {
    const exists = await fs.exists(dir);
    if (!exists) {
      log.warn('Missing directory, creating:', dir);
      await fs.mkdir(dir, { recursive: true });
      repaired++;
    }
  }

  // Vérifier les fichiers essentiels
  for (const file of INITIAL_STRUCTURE.files) {
    const exists = await fs.exists(file.path);
    if (!exists) {
      log.warn('Missing file, creating:', file.path);
      await fs.writeFile(file.path, file.content);
      repaired++;
    }
  }

  timer.end();

  if (repaired > 0) {
    log.info(`Structure verified. Repaired ${repaired} items.`);
  } else {
    log.info('Structure verified. All OK.');
  }
}

/**
 * Initialise le système de fichiers (premier boot ou vérification)
 * @returns {Promise<void>}
 */
export async function initializeFileSystem() {
  const initialized = await isInitialized();

  if (!initialized) {
    log.info('First boot detected. Creating initial structure...');
    await createInitialStructure();
  } else {
    log.info('Existing filesystem found. Verifying...');
    await verifyStructure();
  }
}
